// "How MeeMail is doing" (/api/measure): the owner's verdicts, the week's measures, and the small
// timing reports the app sends to its own server. Nothing here goes anywhere else.
import { queryOptions, useQuery } from "@tanstack/react-query";
import type {
  SessionStartRequest,
  TimingSample,
  VerdictRecorded,
  VerdictRequest,
  WeekStatsReport,
} from "@shared/measure";
import { api, getCsrfToken } from "./api";

export const measureKeys = {
  week: ["measure", "week"] as const,
};

export const measureApi = {
  verdict: (body: VerdictRequest) =>
    api<VerdictRecorded>("/api/measure/verdicts", { method: "POST", body }),
  week: (signal?: AbortSignal) => api<WeekStatsReport>("/api/measure/week", { signal }),
};

export const weekQuery = queryOptions({
  queryKey: measureKeys.week,
  queryFn: ({ signal }) => measureApi.week(signal),
  staleTime: 60_000,
});

export function useWeekStats() {
  return useQuery(weekQuery);
}

/**
 * Fire-and-forget POST for timings: `keepalive` lets it finish while the page hides or unloads
 * (sendBeacon cannot carry the CSRF header). Failures are dropped; a lost sample is not worth a
 * retry. Signed out (no CSRF token yet), nothing is sent.
 */
function postQuietly(path: string, body: unknown): void {
  const csrf = getCsrfToken();
  if (!csrf || typeof fetch !== "function") return;
  void fetch(path, {
    method: "POST",
    keepalive: true,
    credentials: "same-origin",
    headers: { "content-type": "application/json", "x-csrf-token": csrf },
    body: JSON.stringify(body),
  }).catch(() => undefined);
}

export const timingApi = {
  session: (body: SessionStartRequest) => postQuietly("/api/measure/session", body),
  heartbeat: () => postQuietly("/api/measure/heartbeat", {}),
  samples: (samples: TimingSample[]) => postQuietly("/api/measure/samples", { samples }),
};
